/* eslint-disable no-throw-literal */
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import clubAPI from "../api/clubAPI";
import userAPI from "../api/userAPI";
import "../App.scss";
import MultipleEntryPassPaymentLabel from "../components/MultipleEntryPass/MultipleEntryPassPaymentLabel";
import swalDialog from "../helpers/swalErrorPopup";
import DeleteClientCreditCardPopup from "../shared/deleteCreditCardPopup";
import NotificationPopup from "../shared/notificationPopup";
import { PoweredBy } from "../shared/PoweredBy";
import SaveCardCreditPopup from "../shared/saveCardCreditPopup";
import { isLoadChanger } from "../store/mainSlice";
import {
  activeMultipleEntryPassesDetailsSelector,
  addNewMultipleEntryPass,
  canBuyNewMultipleEntryPassSelector,
  checkCanPurchasePass,
  hasActiveMultipleEntryPassSelector,
} from "../store/multipleEntryPassSlice";
import { isPartnerClalit, selectIsMinor, isPartnerOvdimBari } from "../store/partnerStore";
import { updateAllAppParams } from "../store/appParamSlice";
import { updateIsHaveCreditCard } from "../store/userSlice";
import { removeWordIfExists } from "../utils/formatter";
import InfoIcon from "../img/svg/info_dark_blue.svg";

export const Summary = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isClalit = useSelector(isPartnerClalit);
  const isOvdimBari = useSelector(isPartnerOvdimBari);

  const isMinor = useSelector(selectIsMinor);

  const hasActiveMultipleEntryPass = useSelector(hasActiveMultipleEntryPassSelector);

  const canBuyNewMultipleEntryPass = useSelector(canBuyNewMultipleEntryPassSelector);

  const multipleEntryPassDetails = useSelector(activeMultipleEntryPassesDetailsSelector);

  const canBuyPass = !hasActiveMultipleEntryPass && canBuyNewMultipleEntryPass;

  const [openNotificationPopup, setOpenNotificationPopup] = useState(false);
  const [notificationText, setNotificationText] = useState("");
  const [selectedClub, setSelectedClub] = useState(null);
  const cityAndClubs = useSelector((state) => state.appParamSlice.allAppParams);
  const userParams = useSelector((state) => state.userSlice.userData);
  const mainUserParams = useSelector((state) => state.userSlice.mainUserData);
  const isHaveCard = useSelector((state) => state.userSlice.isHaveCreditCard);
  const [selectedMinutes, setSelectedMinutes] = useState({ minute: "00" });
  const location = useLocation();
  const [last4Digit, setLast4Disgit] = useState("");
  const [savedCreditCard, setSavedCreditCard] = useState(false);
  const [isSaveCreditCard, setIsSaveCreditCard] = useState(undefined);
  const [deleteCardCreditPopupOpen, setDeleteCartCreditPopupOpen] = useState(false);
  const [saveCreditCard, setSaveCreditCard] = useState(null);
  const [saveCardCreditPopupOpen, setSaveCartCreditPopupOpen] = useState(false);
  const [isCheckInClub, setIsCheckInClub] = useState(false);

  const [selectedDate, setSelectedDate] = useState({
    date: moment().format("DD/MM/YYYY"),
  });
  const [selectedHours, setSelectedHours] = useState({
    hour: moment().format("HH"),
  });
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(canBuyPass ? "multipleEntryPass" : "creditCard");

  const paymentLabelRef = useRef();

  useEffect(() => {
    checkIsHasCreditCard();
    if (selectedClub) {
      setIsCheckInClub(
        !selectedClub.IsStudio &&
          !selectedClub.IsStudioBoostapp &&
          !selectedClub.IsStudioFizikal &&
          !selectedClub.IsStudioLeap &&
          !selectedClub.IsStudioRbox,
      );
    }
  }, [selectedClub]);

  const handlePayment = async () => {
    if (paymentLabelRef.current) {
      const result = await paymentLabelRef.current.handlePayment();
      dispatch(addNewMultipleEntryPass(result));
      dispatch(
        checkCanPurchasePass({
          token: mainUserParams.Token,
          id: mainUserParams.ID,
          phone: mainUserParams.Phone,
        }),
      );
    }

    await fetchGetWebFullData();
  };

  const fetchGetWebFullData = async () => {
    if (isOvdimBari) {
      try {
        const param = await clubAPI.getWebFullData({
          binID: userParams.BinID,
          token: mainUserParams.Token,
          id: mainUserParams.ID,
          phone: mainUserParams.Phone,
        });
        if (param.data.Error !== 0) {
          throw { code: param.data.Error, mess: param.data.Message };
        }
        dispatch(updateAllAppParams(JSON.parse(param.data.Data)));
      } catch (err) {
        swalDialog(err);
      }
    }
  };

  const checkIsHasCreditCard = async () => {
    const chosenClub = cityAndClubs?.ClubList.find((el) => el.RecordID == location?.state?.clubId);
    setSelectedClub(chosenClub);
    try {
      dispatch(isLoadChanger(true));

      let isHaveCreditCard = await userAPI.isClientHaveCreditCard({
        token: mainUserParams.Token,
        id: mainUserParams.ID,
        phone: mainUserParams.Phone,
      });

      if (isHaveCreditCard.data.Error != 0) {
        throw {
          code: isHaveCreditCard.data.Error,
          mess: isHaveCreditCard.data.Message,
        };
      }

      const jsonData = JSON.parse(isHaveCreditCard.data.Data);

      setSavedCreditCard(jsonData.IsCardSaved);

      dispatch(updateIsHaveCreditCard(jsonData.HasCreditCard));

      setLast4Disgit(jsonData.Last4Digit);

      dispatch(isLoadChanger(false));
    } catch (err) {
      dispatch(isLoadChanger(false));
      swalDialog(err);
    }
  };

  const formatDate = (originalDate) => {
    const months = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
    const [datePart, timePart] = originalDate?.split(" ");
    const [day, month, year] = datePart?.split(".");
    const [hours, minutes] = timePart?.split(":");
    const formattedDate = ` ${t("ON_DATE")} ${day}/${months[month - 1]}/${year.slice(-2)} ${t(
      "IN_A",
    )}${hours}:${minutes}`;
    return formattedDate;
  };

  const deleteCreditCardHandle = async () => {
    const result = await userAPI.deleteClientCreditCard({
      token: mainUserParams.Token,
      id: mainUserParams.ID,
      phone: mainUserParams.Phone,
    });

    if (result.data.Error != 0) {
      throw {
        code: result.data.Error,
        mess: result.data.Message,
      };
    }
  };

  const handleWebClubOrder = async (selectedClub, isSaveCreditCardLocal = false) => {
    try {
      dispatch(isLoadChanger(true));

      const orderParams = {
        token: mainUserParams.Token,
        id: mainUserParams.ID,
        phone: mainUserParams.Phone,
        cardNumber: userParams.CardNumber,
        source: isClalit ? null : (mainUserParams.Source ?? null),
        terminalID: selectedClub.TerminalID,
        binType: selectedClub.BinType,
        clubID: selectedClub.RecordID,
        isRbox:
          selectedClub.IsStudioRbox ||
          selectedClub.IsStudioFizikal ||
          selectedClub.IsStudioLeap ||
          selectedClub.IsStudioBoostapp,
      };

      if (location.state?.type === "book") {
        Object.assign(orderParams, {
          preOrderDate: location.state.selectedLesson.LessonStartDate,
          rboxLessonID: location.state.selectedLesson.RboxLessonID,
          lessonName: location.state.selectedLesson.LessonName,
          lessonStartDate: location.state.selectedLesson.LessonStartDate,
          lessonEndDate: location.state.selectedLesson.LessonEndDate,
          coachName: location.state.selectedLesson.CoachName,
          cancelationTime: location.state.selectedLesson.CancelationTime,
          isCancelAllow: location.state.selectedLesson.IsCancelAllow,
        });
      } else {
        Object.assign(orderParams, {
          preOrderDate: `${location.state.date.split("/").join(".")} ${location.state.hours}:${location.state.minutes}`,
          isOnlyCheckWithoutTransaction: false,
        });
      }

      const result = await clubAPI.createWebClubOrder(orderParams);

      if (result.data.Error != 0) {
        throw {
          code: result.data.Error,
          mess: result.data.Message,
        };
      }

      await fetchGetWebFullData();

      await userAPI.saveDeleteClientCreditCard({
        token: mainUserParams.Token,
        id: mainUserParams.ID,
        phone: mainUserParams.Phone,
        saveCreditCard: isMinor ? false : isSaveCreditCardLocal,
      });

      dispatch(isLoadChanger(false));
      return result;
    } catch (err) {
      dispatch(isLoadChanger(false));
      swalDialog(err);
      return null;
    }
  };

  const redirectWithCondition = async (saveCreditCardDetails) => {
    dispatch(isLoadChanger(true));
    try {
      const selectedClub = cityAndClubs.ClubList.find((el) => el.RecordID === location.state.clubId);
      if (
        isHaveCard ||
        ((isClalit || isOvdimBari) && hasActiveMultipleEntryPass && multipleEntryPassDetails.NumberOfEntries < 4)
      ) {
        if (location.state?.type === "checkin") {
          dispatch(isLoadChanger(true));

          if (savedCreditCard) {
            const result = await clubAPI.webClubEntry({
              token: mainUserParams.Token,
              id: mainUserParams.ID,
              phone: mainUserParams.Phone,
              cardNumber: userParams.CardNumber,
              source: isClalit ? null : (mainUserParams.Source ?? null),
              terminalID: selectedClub.TerminalID,
              clubID: selectedClub.RecordID,
              binType: selectedClub.BinType,
            });

            if (result.data.Error != 0) {
              throw {
                code: result.data.Error,
                mess: result.data.Message,
              };
            }

            await fetchGetWebFullData();

            await userAPI.saveDeleteClientCreditCard({
              token: mainUserParams.Token,
              id: mainUserParams.ID,
              phone: mainUserParams.Phone,
              saveCreditCard: isMinor ? false : saveCreditCardDetails,
            });

            dispatch(isLoadChanger(false));

            navigate("/orderConfirmation", {
              state: {
                date: moment().format("DD-MM-YYYY"),
                hours: moment().format("HH"),
                minutes: moment().format("mm"),
                clubId: selectedClub.RecordID,
                clubName: selectedClub.Name,
                nowDate: true,
              },
            });
          } else if (savedCreditCard === false) {
            dispatch(isLoadChanger(true));
            const result = await clubAPI.webClubEntry({
              token: mainUserParams.Token,
              id: mainUserParams.ID,
              phone: mainUserParams.Phone,
              cardNumber: userParams.CardNumber,
              source: isClalit ? null : (mainUserParams.Source ?? null),
              terminalID: selectedClub.TerminalID,
              clubID: selectedClub.RecordID,
              binType: selectedClub.BinType,
            });

            if (result.data.Error != 0) {
              throw {
                code: result.data.Error,
                mess: result.data.Message,
              };
            }

            await fetchGetWebFullData();

            await userAPI.saveDeleteClientCreditCard({
              token: mainUserParams.Token,
              id: mainUserParams.ID,
              phone: mainUserParams.Phone,
              saveCreditCard: isMinor ? false : saveCreditCardDetails,
            });

            dispatch(isLoadChanger(false));

            navigate("/orderConfirmation", {
              state: {
                date: moment().format("DD-MM-YYYY"),
                hours: moment().format("HH"),
                minutes: moment().format("mm"),
                clubId: selectedClub.RecordID,
                clubName: selectedClub.Name,
                nowDate: true,
              },
            });
          } else {
            dispatch(isLoadChanger(true));
            const result = await clubAPI.webClubEntry({
              token: mainUserParams.Token,
              id: mainUserParams.ID,
              phone: mainUserParams.Phone,
              cardNumber: userParams.CardNumber,
              source: isClalit ? null : (mainUserParams.Source ?? null),
              terminalID: selectedClub.TerminalID,
              clubID: selectedClub.RecordID,
              binType: selectedClub.BinType,
            });

            if (result.data.Error != 0) {
              throw {
                code: result.data.Error,
                mess: result.data.Message,
              };
            }

            await fetchGetWebFullData();

            await userAPI.saveDeleteClientCreditCard({
              token: mainUserParams.Token,
              id: mainUserParams.ID,
              phone: mainUserParams.Phone,
              saveCreditCard: isMinor ? false : saveCreditCardDetails,
            });

            dispatch(isLoadChanger(false));

            navigate("/orderConfirmation", {
              state: {
                date: moment().format("DD-MM-YYYY"),
                hours: moment().format("HH"),
                minutes: moment().format("mm"),
                clubId: selectedClub.RecordID,
                clubName: selectedClub.Name,
                nowDate: true,
              },
            });
          }
        } else if (location.state?.type === "invite") {
          if (savedCreditCard) {
            dispatch(isLoadChanger(true));
            const succeedToOrder = await handleWebClubOrder(selectedClub, saveCreditCardDetails);
            if (succeedToOrder?.data?.Error == 0) {
              dispatch(isLoadChanger(false));
              const cancellationTime = getCancellationTime(succeedToOrder);
              navigate("/orderConfirmation", {
                state: {
                  date: location.state?.date,
                  hours: location.state.hours,
                  minutes: location.state.minutes,
                  clubId: selectedClub.RecordID,
                  clubName: selectedClub.Name,
                  nowDate: true,
                  cancellationTime: cancellationTime,
                },
              });
            }
          } else if (savedCreditCard === false) {
            dispatch(isLoadChanger(true));
            const succeedToOrder = await handleWebClubOrder(selectedClub, saveCreditCardDetails);
            if (succeedToOrder?.data?.Error == 0) {
              dispatch(isLoadChanger(false));

              const cancellationTime = getCancellationTime(succeedToOrder);
              navigate("/orderConfirmation", {
                state: {
                  date: location.state?.date,
                  hours: location.state.hours,
                  minutes: location.state.minutes,
                  clubId: selectedClub.RecordID,
                  clubName: selectedClub.Name,
                  nowDate: true,
                  cancellationTime: cancellationTime,
                },
              });
            }
          } else {
            dispatch(isLoadChanger(true));
            const succeedToOrder = await handleWebClubOrder(selectedClub, saveCreditCardDetails);
            if (succeedToOrder?.data?.Error == 0) {
              dispatch(isLoadChanger(false));

              const cancellationTime = getCancellationTime(succeedToOrder);
              navigate("/orderConfirmation", {
                state: {
                  date: location.state?.date,
                  hours: location.state.hours,
                  minutes: location.state.minutes,
                  clubId: selectedClub.RecordID,
                  clubName: selectedClub.Name,
                  nowDate: true,
                  cancellationTime: cancellationTime,
                },
              });
            }
          }
        } else if (location.state?.type === "book") {
          const selectedClub = cityAndClubs.ClubList.find((el) => el.RecordID === location.state.clubId);
          dispatch(isLoadChanger(true));
          if (savedCreditCard) {
            const succeedToOrder = await handleWebClubOrder(selectedClub, saveCreditCardDetails);
            if (succeedToOrder?.data?.Error == 0) {
              dispatch(isLoadChanger(false));
              const cancellationTime = getCancellationTime(succeedToOrder);
              navigate("/orderConfirmation", {
                state: {
                  date: location.state?.dateFull,
                  clubId: selectedClub.RecordID,
                  clubName: selectedClub.Name,
                  nowDate: false,
                  cancellationTime: cancellationTime,
                },
              });
            }
          } else if (savedCreditCard === false) {
            const succeedToOrder = await handleWebClubOrder(selectedClub, saveCreditCardDetails);
            if (succeedToOrder?.data?.Error == 0) {
              dispatch(isLoadChanger(false));
              const cancellationTime = getCancellationTime(succeedToOrder);
              navigate("/orderConfirmation", {
                state: {
                  date: location.state?.dateFull,
                  clubId: selectedClub.RecordID,
                  clubName: selectedClub.Name,
                  nowDate: false,
                  cancellationTime: cancellationTime,
                },
              });
            }
          } else {
            const succeedToOrder = await handleWebClubOrder(selectedClub, saveCreditCardDetails);
            if (succeedToOrder?.data?.Error == 0) {
              dispatch(isLoadChanger(false));
              const cancellationTime = getCancellationTime(succeedToOrder);
              navigate("/orderConfirmation", {
                state: {
                  date: location.state?.dateFull,
                  clubId: selectedClub.RecordID,
                  clubName: selectedClub.Name,
                  nowDate: false,
                  cancellationTime: cancellationTime,
                },
              });
            }
          }
        }
      } else {
        navigate("/addCard", {
          state: {
            date: selectedDate.date,
            hours: selectedHours.hour,
            minutes: selectedMinutes.minute,
            clubId: location.state.clubId,
            clubName: location.state?.clubName,
            route: "orderConfirmation",
          },
        });
      }
      dispatch(isLoadChanger(false));
    } catch (err) {
      dispatch(isLoadChanger(false));
      setOpenNotificationPopup(true);
      setNotificationText(removeWordIfExists(err.mess, t("IMPORTANT")));
    }
    if (isClalit || isOvdimBari) {
      dispatch(
        checkCanPurchasePass({
          token: mainUserParams.Token,
          id: mainUserParams.ID,
          phone: mainUserParams.Phone,
        }),
      );
    }
  };

  const openIsCreditSaved = async () => {
    // Not saving credit card for "Minors" (MC-631)
    if (isMinor) {
      setIsSaveCreditCard(false);
      await buyNewMultipleEntryPass();
      redirectWithCondition(false);
    } else if (savedCreditCard && !isClalit && !isOvdimBari) {
      setIsSaveCreditCard(true);
      redirectWithCondition(true);
    } else {
      if (hasActiveMultipleEntryPass && (isClalit || isOvdimBari)) {
        setIsSaveCreditCard(true);
        redirectWithCondition(true);
      } else {
        setDeleteCartCreditPopupOpen(true);
      }
    }
  };

  const buyNewMultipleEntryPass = async () => {
    if ((isClalit || isOvdimBari) && selectedPaymentMethod === "multipleEntryPass" && !hasActiveMultipleEntryPass) {
      try {
        const result = await handlePayment();
      } catch (error) {
        return true;
      }
    } else return true;
  };

  const handlePaymentMethodChange = (method) => {
    setSelectedPaymentMethod(method);
  };

  const calculatePrice = () => {
    return selectedPaymentMethod === "multipleEntryPass" && isClalit
      ? 90
      : selectedPaymentMethod === "multipleEntryPass" && isOvdimBari
        ? 160
        : selectedClub?.Price;
  };

  const getCancellationTime = (succeedToOrder) => {
    if (!succeedToOrder || !succeedToOrder.data || !succeedToOrder.data.Data) {
      return 0;
    }

    try {
      const jsonData = JSON.parse(succeedToOrder.data.Data);
      if (jsonData && jsonData[0]) {
        const cancellationTime = jsonData[0].CancellationTime;
        return cancellationTime;
      }
      return 0;
    } catch (error) {
      return 0;
    }
  };

  return (
    <div className="main-page-container">
      <div className={isClalit || isOvdimBari ? "fit-selector-with-background" : "fit-selector-with-background-wow"}>
        <div className="main-page-btn-container">
          <div>
            <div className={`bottom-popup`}>
              <div className="popup-content">
                <div
                  className={"popup-content-wrapper"}
                  style={
                    hasActiveMultipleEntryPass && multipleEntryPassDetails.NumberOfEntries < 4 ? { flexGrow: 0.3 } : {}
                  }
                >
                  <div>
                    <div className="mobile-popup-header">
                      <p className="mobile-popup-header-text">{t("PURCHASE_SUMMARY")}</p>
                    </div>
                    <div
                      className={isClalit || isOvdimBari ? "mobile-popup-header-info" : "mobile-popup-header-info-wow"}
                    >
                      <p style={{ direction: "rtl" }}>{selectedClub?.Name}</p>

                      {location.state?.type === "checkin" ? (
                        ""
                      ) : location.state?.type === "book" && location.state?.dateFull ? (
                        formatDate(location.state?.dateFull)
                      ) : (
                        <>
                          {t("ON_DATE")} {location.state.date} {t("IN_A")}
                          {location.state.hours}:{location.state.minutes}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mobile-popup-content-wrapper">
                    <div className="mobile-popup-text-content-container">
                      <MultipleEntryPassPaymentLabel
                        ref={paymentLabelRef}
                        last4Digit={last4Digit}
                        selectedPaymentMethod={selectedPaymentMethod}
                        onPaymentMethodChange={handlePaymentMethodChange}
                        buyNewPass={false}
                      />
                      {(!hasActiveMultipleEntryPass ||
                        (hasActiveMultipleEntryPass &&
                          (multipleEntryPassDetails === undefined ||
                            multipleEntryPassDetails.NumberOfEntries >= 4))) && (
                        <div
                          className={
                            isClalit || isOvdimBari ? "mobile-popup-text-footer-purchase" : "mobile-popup-text-footer"
                          }
                        >
                          <p className="mobile-popup-text-thin">{t("TOTAL_PRICE")}:</p>
                          {calculatePrice() > 0 && <p>₪{calculatePrice()}</p>}
                        </div>
                      )}
                      {!isClalit && !isOvdimBari && isCheckInClub && (
                        <div className="summary-checkin-club-message">
                          <img src={InfoIcon} alt="info" />
                          {t("SUMMARY_CHECKIN_CLUB_CANCEL_MESSAGE")}
                        </div>
                      )}
                      {(isClalit || isOvdimBari) && !hasActiveMultipleEntryPass && (
                        <span className="payment-method-description">
                          {selectedPaymentMethod === "multipleEntryPass" && (
                            <>{t("PAYMENT_MULTIPLE_ENTRY_PASS_DESCRIPTION")}</>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mobile-popup-btn-block">
                  <button
                    style={{ fontSize: "18px", fontWeight: 700, width: "100%" }}
                    className={
                      isClalit || isOvdimBari
                        ? "main-page-btn big-button very-big-button lightred-btn"
                        : "payment-button-wow"
                    }
                    onClick={() => openIsCreditSaved()}
                  >
                    {t("PAY")}
                  </button>
                  {isClalit && <PoweredBy />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SaveCardCreditPopup
        isOpen={saveCardCreditPopupOpen}
        setIsOpen={setSaveCartCreditPopupOpen}
        cancelText={t("DO_NOT_SAVE")}
        confirmText={t("CONFIRM_GREAT")}
        headerText={t("SAVE_PAYMENT_METHOD")}
        bodyText={t("SAVE_CARD_MESSAGE")}
        // handleConfirm={() => setSaveCreditCard(true)}
        handleCancel={() => setSaveCreditCard(false)}
      />
      <NotificationPopup
        isOpen={openNotificationPopup}
        setIsOpen={setOpenNotificationPopup}
        bodyTopText={t("IMPORTANT")}
        bodyText={notificationText}
        btnText={t("CLOSE")}
      />
      <DeleteClientCreditCardPopup
        isOpen={deleteCardCreditPopupOpen}
        setIsOpen={setDeleteCartCreditPopupOpen}
        cancelText={t("CONFIRM_GREAT")}
        deleteText={t("DO_NOT_SAVE")}
        headerText={t("SAVE_PAYMENT_METHOD")}
        bodyText={t("SAVE_CARD_REUSE_MESSAGE")}
        handleDelete={async () => {
          setIsSaveCreditCard(false);
          setDeleteCartCreditPopupOpen(false);
          await buyNewMultipleEntryPass();
          redirectWithCondition(false);
        }}
        handleCancel={async () => {
          setIsSaveCreditCard(true);
          setDeleteCartCreditPopupOpen(false);
          await buyNewMultipleEntryPass();
          redirectWithCondition(true);
        }}
      />
    </div>
  );
};
